import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { NAVIGATION_COPY } from '@/locales/en'
import { useUpdateRegistrationAddress } from '@/account/accountQueries'
import { SearchableAddressForm } from '@/user/address/SearchableAddressForm'
import { AddressFormSchema, AddressDetails } from '@/common/types'
import { AccountSettingsFormLayout, AccountSettingsSuccessLayout } from './AccountSettingsFormLayout'

const EMPTY_ADDRESS: AddressDetails = {
    streetNumber: '',
    streetName: '',
    city: '',
    state: '',
    postCode: '',
    country: '',
}

type Props = {
    accountId: string
    initialAddress?: AddressDetails
    onSave: () => void
    onCancel: () => void
}

export default function RegistrationAddressView({ accountId, initialAddress, onSave, onCancel }: Props) {
    const copy = NAVIGATION_COPY.account.registrationAddressForm
    const successCopy = NAVIGATION_COPY.account.registrationAddressSuccess
    const [savedAddress, setSavedAddress] = useState<AddressDetails | null>(null)
    const updateRegistrationAddress = useUpdateRegistrationAddress()

    const {
        register,
        trigger,
        setValue,
        watch,
        formState: { errors },
    } = useForm<AddressFormSchema>({
        values: {
            residential: initialAddress || EMPTY_ADDRESS,
            postal: {},
        },
        resetOptions: {
            keepDirtyValues: true,
        },
    })

    const address = watch('residential')
    const isLoading = updateRegistrationAddress.isPending

    const isValid = !!(
        address?.streetNumber &&
        address?.streetName &&
        address?.city &&
        address?.state &&
        address?.postCode &&
        address?.country
    )

    const handleSave = async () => {
        if (isValid && address) {
            const registrationAddress: AddressDetails = {
                addressId: address.addressId,
                addressPrefix: address.addressPrefix || undefined,
                streetNumber: address.streetNumber,
                streetName: address.streetName,
                streetType: address.streetType,
                city: address.city,
                state: address.state,
                postCode: address.postCode,
                country: address.country,
            }

            try {
                await updateRegistrationAddress.mutateAsync({
                    accountId,
                    address: registrationAddress,
                })
                setSavedAddress(registrationAddress)
            } catch {
                // Error toast is handled by the mutation hook
            }
        }
    }

    if (savedAddress) {
        return (
            <AccountSettingsSuccessLayout
                title={successCopy.pageTitle}
                onClose={onSave}
                closeLabel={successCopy.backToAccount}
            />
        )
    }

    return (
        <AccountSettingsFormLayout
            title={copy.pageTitle}
            description={copy.description}
            onCancel={onCancel}
            onSave={handleSave}
            isLoading={isLoading}
            isValid={isValid}
            cancelLabel={copy.cancel}
            saveLabel={copy.save}
        >
            <div className="w-full flex flex-col">
                <SearchableAddressForm
                    type="residential"
                    register={register}
                    trigger={trigger}
                    setValue={setValue}
                    errors={errors.residential}
                    defaultValues={initialAddress}
                />
            </div>
        </AccountSettingsFormLayout>
    )
}
